// TimeWheel — iOS-style date/hour/minute pickers for ETD entry, in 24h
// local time. Uses three native <select>s under the hood — the iOS
// scroll-wheel UX is inherited automatically on iPad and iPhone.
//
// Value contract: parent owns the ISO timestamp and passes a
// (date, hour, minute) breakdown derived from it. We emit changes via
// onChange(iso) where iso is the new ISO timestamp in UTC for storage,
// computed from the local-time selection.

import { useEffect, useMemo, useState } from 'react';

interface Props {
  /** Current ISO timestamp, or null for "no ETD set". */
  value: string | null;
  /** Called with a new ISO timestamp (or null to clear). */
  onChange: (iso: string | null) => void;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

function buildDateOptions(): Array<{ value: string; label: string }> {
  // Today + next 14 days. Stored as YYYY-MM-DD local. Display: "Today",
  // "Tomorrow", or weekday + month + day.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const out: Array<{ value: string; label: string }> = [];
  const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  for (let i = 0; i < 15; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const value =
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let label: string;
    if (i === 0) label = 'Today';
    else if (i === 1) label = 'Tomorrow';
    else label = fmt.format(d);
    out.push({ value, label });
  }
  return out;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function decompose(iso: string): { date: string; hour: number; minute: number } {
  const d = new Date(iso);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

export function composeIsoFromParts(date: string, hour: number, minute: number): string {
  // date is YYYY-MM-DD interpreted as local. Build a Date and emit ISO
  // with the device's local timezone offset baked in (UTC string).
  const [y, m, dd] = date.split('-').map(Number);
  const local = new Date(y, m - 1, dd, hour, minute, 0, 0);
  return local.toISOString();
}

/**
 * Round 'now' up to the next 5-minute slot, rolling over hour and date as
 * needed. Returns a fully-valid (date, hour, minute) triple in local time.
 * Avoids the minute=60 bug where Math.ceil(58/5)*5=60 produced a value
 * that wasn't in the minute <select>.
 */
export function nextFiveMinuteSlotLocal(): { date: string; hour: number; minute: number } {
  const d = new Date();
  // Add up to +5 minutes to land on the next slot, then zero seconds.
  const m = d.getMinutes();
  const bump = (5 - (m % 5)) % 5 || 5; // always advance at least 5 min
  d.setMinutes(m + bump, 0, 0);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

export default function TimeWheel({ value, onChange }: Props) {
  // Re-tick the option list once a minute so 'Today / Tomorrow' don't go
  // stale across midnight while the panel sits open. Cheap.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const dateOptions = useMemo(() => {
    const base = buildDateOptions();
    // If the saved ETD is outside today+14, inject the saved date so the
    // <select> always has a matching option.
    if (value) {
      const dpart = decompose(value).date;
      if (!base.some((o) => o.value === dpart)) {
        const d = new Date(value);
        const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
        base.unshift({ value: dpart, label: fmt.format(d) });
      }
    }
    return base;
    // Re-derive when value changes or the day rolls over (setTick).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const fallback = useMemo(nextFiveMinuteSlotLocal, []);
  const parts = value ? decompose(value) : null;
  const date = parts?.date ?? fallback.date;
  const hour = parts?.hour ?? fallback.hour;
  const minute = parts?.minute ?? fallback.minute;

  // (2026-05-18 boss report) When value is null, the wheel renders the
  // next-five-minute slot as a visual default — but onChange was never
  // fired, so the parent's ETD stayed null and saved flights had no ETD.
  // Push the displayed default into the parent on mount so what the user
  // SEES is what gets saved. Idempotent: only fires when value is null.
  useEffect(() => {
    if (value === null) {
      onChange(composeIsoFromParts(fallback.date, fallback.hour, fallback.minute));
    }
    // We only want this on mount; subsequent value transitions to null
    // (e.g. user clicks "Clear ETD") are intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (next: { date?: string; hour?: number; minute?: number }) => {
    onChange(composeIsoFromParts(next.date ?? date, next.hour ?? hour, next.minute ?? minute));
  };

  return (
    <div className="grid grid-cols-3 gap-2">
      <Wheel
        label="Date"
        value={date}
        onChange={(v) => update({ date: v })}
        options={dateOptions}
      />
      <Wheel
        label="Hour"
        value={String(hour)}
        onChange={(v) => update({ hour: Number(v) })}
        options={HOURS.map((h) => ({ value: String(h), label: pad(h) }))}
      />
      <Wheel
        label="Minute"
        value={String(minute)}
        onChange={(v) => update({ minute: Number(v) })}
        options={MINUTES.map((m) => ({ value: String(m), label: pad(m) }))}
      />
    </div>
  );
}

function Wheel({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-widest text-cockpit-300 mb-1">{label.toUpperCase()}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-cockpit-100 text-sm px-2 py-2"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
